import { z } from 'zod/v4';

const SHARED_SCHEMA = z.object({
  templateName: z.string().optional(),
  templateDescription: z.string().optional(),
  // Encoded user or Team assignee (see taskAssignee.ts).
  assignedTo: z.string().nullable().optional(),
  dueDate: z.date().optional(),
  notes: z.string().optional(),
  // Template-mode only: encoded assignee (see templateAssignee.ts) and
  // deadline offset in days for tasks created from the template.
  assignee: z.string().nullable().optional(),
  deadlineOffsetDays: z.number().int().min(0).nullable().optional(),
});

export const CHECKLIST_SCHEMA = SHARED_SCHEMA.extend({
  type: z.literal('checklist'),
  name: z.string().min(1),
  checklistItems: z.array(
    z.object({
      publicId: z.string().optional(),
      label: z.string().min(1),
    })
  ),
});

export const FORM_TASK_SCHEMA = SHARED_SCHEMA.extend({
  type: z.literal('form'),
  formId: z.number({ message: 'A form must be selected' }).optional(),
});

export const CUSTOM_FIELD_SCHEMA = SHARED_SCHEMA.extend({
  type: z.literal('custom-fields'),
  customFieldIdentifier: z.string().optional(),
  customFieldGroupId: z.string().optional(),
});

export const TASK_SCHEMA = z
  .discriminatedUnion('type', [
    CHECKLIST_SCHEMA,
    FORM_TASK_SCHEMA,
    CUSTOM_FIELD_SCHEMA,
  ])
  .superRefine((data, ctx) => {
    if (data.type === 'form' && data.formId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'A form must be selected',
        path: ['formId'],
      });
    }
    if (data.type === 'custom-fields') {
      if (!data.customFieldIdentifier) {
        ctx.addIssue({
          code: 'custom',
          message: 'Required',
          path: ['customFieldIdentifier'],
        });
      }
    }
  });

export type CreateEditTaskFormValues = z.infer<typeof TASK_SCHEMA>;

export type CheckListValues = z.infer<typeof CHECKLIST_SCHEMA>;
export type FormTaskValues = z.infer<typeof FORM_TASK_SCHEMA>;
export type CustomFieldTaskValues = z.infer<typeof CUSTOM_FIELD_SCHEMA>;
